import { IAnswerType, IOption, IEducation, IContactInfo, IAvailability } from '../interfaces';

/**
 * Type guards to check the type of answer
 */
const isString = (value: unknown): value is string => typeof value === 'string';

const isOption = (value: unknown): value is IOption =>
  typeof value === 'object' && value !== null && 'id' in value && 'label' in value;

const isOptionArray = (value: unknown[]): value is IOption[] =>
  value.length > 0 && isOption(value[0]);

const isEducationArray = (value: unknown[]): value is IEducation[] =>
  value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'school' in value[0];

const isContactInfo = (value: unknown): value is IContactInfo =>
  typeof value === 'object' &&
  value !== null &&
  'first_name' in value &&
  'last_name' in value &&
  'email' in value;

const isAvailability = (value: unknown): value is IAvailability =>
  typeof value === 'object' && value !== null && 'monday' in value && 'sunday' in value;

/**
 * Formats an answer value based on its type to ensure it's compatible
 * with the expected input format.
 *
 * @param answer The answer value to format
 * @returns Formatted answer value as string
 */
const formatAnswer = (answer: IAnswerType | undefined) => {
  if (!answer) return { answer: '' };

  if (isString(answer)) {
    return { answer };
  }

  if (Array.isArray(answer)) {
    if (answer.length === 0) return { answer: '' };

    if (isOptionArray(answer)) {
      return {
        answer: answer.map((option) => ({ id: option.id.toString(), label: option.label })),
      };
    }

    if (isEducationArray(answer)) {
      return { education: answer };
    }
  }

  if (isOption(answer)) {
    return { answer: { id: answer.id.toString(), label: answer.label } };
  }

  if (isContactInfo(answer)) {
    return { answer };
  }

  if (isAvailability(answer)) {
    return { availability: answer };
  }
};

export { formatAnswer };
